import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEffectiveAccess } from "@/hooks/useEffectiveAccess";
import { useInstitutionViewerCheck } from "@/hooks/useInstitutionViewerCheck";
import { useInstitutionAdminCheck } from "@/hooks/useInstitutionAdminCheck";
import { useSchoolAdminCheck } from "@/hooks/useSchoolAdminCheck";
import { supabase } from "@/integrations/supabase/client";
import { resolveInstitutionalRoute, logAccessDecision } from "@/lib/institutional-access";
import { logEffectiveAccess } from "@/lib/access-engine";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";


const LOADING_TIMEOUT = 10000;

/**
 * ProtectedRoute — thin wrapper that delegates ALL access decisions
 * to the unified access engine (useEffectiveAccess).
 *
 * Remaining local logic:
 *  - Institution viewer/admin role redirects (these are navigation-only,
 *    not access decisions, and don't exist in the engine).
 */
export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const access = useEffectiveAccess();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Role-based navigation redirects (institution viewer/admin)
  const { isInstitutionViewer, loading: viewerLoading } = useInstitutionViewerCheck();
  const { isInstitutionAdmin, loading: instAdminLoading } = useInstitutionAdminCheck();
  const { isSchoolAdmin, loading: schoolAdminLoading } = useSchoolAdminCheck();

  const hasNavigated = useRef(false);
  const [timedOut, setTimedOut] = React.useState(false);

  const roleNavLoading = viewerLoading || instAdminLoading || schoolAdminLoading;
  const isLoading = access.status === "loading" || roleNavLoading;

  // Safety timeout
  React.useEffect(() => {
    if (!isLoading) { setTimedOut(false); return; }
    const timer = setTimeout(() => setTimedOut(true), LOADING_TIMEOUT);
    return () => clearTimeout(timer);
  }, [isLoading]);

  // Reset navigation guard on user change
  useEffect(() => {
    if (access.input.user) hasNavigated.current = false;
  }, [access.input.user?.id]);

  // ── Engine-driven redirect (guest, blocked, email unconfirmed) ──
  useEffect(() => {
    if (access.status === "loading" || roleNavLoading) return;
    if (access.redirectTo && !hasNavigated.current) {
      hasNavigated.current = true;
      logEffectiveAccess("ProtectedRoute", access, { action: "redirect" });
      navigate(access.redirectTo, { replace: true });
    }
  }, [access.status, access.redirectTo, roleNavLoading, navigate]);

  // ── Institution viewer redirect (navigation, not access) ──
  useEffect(() => {
    if (access.status !== "ready" || roleNavLoading || hasNavigated.current) return;
    if (access.role === "admin") return; // admins go through

    if (isInstitutionViewer && !isInstitutionAdmin && !isSchoolAdmin) {
      hasNavigated.current = true;
      supabase
        .from("user_cohorts")
        .select("cohort_id")
        .eq("user_id", access.input.user!.id)
        .limit(1)
        .then(({ data }) => {
          if (data?.length) navigate(`/institution/${data[0].cohort_id}`, { replace: true });
          else navigate("/auth/login", { replace: true });
        });
      return;
    }

    if (isInstitutionAdmin || isSchoolAdmin) {
      hasNavigated.current = true;
      navigate("/admin-cohorts", { replace: true });
      return;
    }

    // Institutional student route gating (baseline, program-not-started)
    if (access.role === "institutional" || access.role === "cohort") {
      const routeDecision = resolveInstitutionalRoute({
        isInstitutionalStudent: access.input.isInstitutionalStudent,
        isProgramActive: access.input.isProgramActive,
        isProgramNotStarted: access.input.isProgramNotStarted,
        isBaselineRequired: access.input.isBaselineRequired,
        cohortEnded: access.input.cohortEnded,
        isAdmin: false,
        pathname: location.pathname,
      });

      logAccessDecision("ProtectedRoute", routeDecision.reason, {
        pathname: location.pathname,
        allow: routeDecision.allow,
        redirectTo: routeDecision.redirectTo,
      });

      if (!routeDecision.allow && routeDecision.redirectTo && !hasNavigated.current) {
        hasNavigated.current = true;
        navigate(routeDecision.redirectTo, { replace: true });
      }
    }
  }, [access, roleNavLoading, isInstitutionViewer, isInstitutionAdmin, isSchoolAdmin, navigate, location.pathname]);

  // ── Render ──
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{t("common.loading")}</p>
          {timedOut && (
            <Button variant="outline" size="sm" className="mt-4 gap-2" onClick={() => window.location.reload()}>
              <RefreshCw className="w-4 h-4" />
              {t("common.retry") || "Reintentar"}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Denied with redirect already triggered
  if (access.status === "denied") return null;

  return (
    <>
      {children}
    </>
  );
};

